import { create } from "zustand";
import {
  importCgaChangeRequests,
  pollCgaIngestionJob,
  type CgaIngestionJobResponse,
} from "../app/pages/ChangeGovernanceAssuranceReadiness/services/cgarApi";
import { useToastStore } from "./toastStore";

export interface ActiveCgaIngestionJob {
  jobId: string;
  fileName: string;
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "COMPLETED_WITH_ERRORS" | "FAILED";
  percentage: number;
  displayedPct: number;
  currentStage?: string;
  totalCrCount: number;
  completedCrCount: number;
  failedCrCount: number;
  totalFileCount: number;
  processedFileCount: number;
  ocrPagesCompleted: number;
  ocrPagesTotal: number;
  elapsedTimeMs: number;
  errorSummary?: string;
  createdAt: string;
}

interface CgaIngestionState {
  jobs: Record<string, ActiveCgaIngestionJob>;
  startIngestion: (file: File, orgId: string) => Promise<void>;
  dismiss: (jobId: string) => void;
}

const _intervals = new Map<string, ReturnType<typeof setInterval>>();

function stopPolling(jobId: string) {
  const id = _intervals.get(jobId);
  if (id !== undefined) {
    clearInterval(id);
    _intervals.delete(jobId);
  }
}

export const useCgaIngestionStore = create<CgaIngestionState>((set, get) => ({
  jobs: {},

  startIngestion: async (file: File, orgId: string) => {
    try {
      const res = await importCgaChangeRequests(file, orgId, "AUTO");
      const jobId = res.jobId;

      if (!jobId) {
        useToastStore.getState().showError("Import Failed", "No job ID returned from server.");
        return;
      }

      useToastStore.getState().showSuccess(
        "Import Started",
        `Uploading "${file.name}". Ingestion progress will appear on the bottom right.`
      );

      const initialJob: ActiveCgaIngestionJob = {
        jobId,
        fileName: file.name,
        status: res.status || "PROCESSING",
        percentage: res.percentage || 0,
        displayedPct: res.percentage || 0,
        currentStage: res.currentStage || "PREPARING",
        totalCrCount: res.totalCrCount ?? 0,
        completedCrCount: res.completedCrCount ?? 0,
        failedCrCount: res.failedCrCount ?? 0,
        totalFileCount: res.totalFileCount ?? 0,
        processedFileCount: res.processedFileCount ?? 0,
        ocrPagesCompleted: res.ocrPagesCompleted ?? 0,
        ocrPagesTotal: res.ocrPagesTotal ?? 0,
        elapsedTimeMs: res.elapsedTimeMs ?? 0,
        errorSummary: res.errorSummary,
        createdAt: res.createdAt || new Date().toISOString(),
      };

      set((state) => ({
        jobs: {
          ...state.jobs,
          [jobId]: initialJob,
        },
      }));

      const pollFn = async () => {
        try {
          const updated = await pollCgaIngestionJob(jobId);
          if (!updated) return;

          const prevDisplayedPct = get().jobs[jobId]?.displayedPct ?? 0;
          const serverPct = updated.percentage ?? 0;
          const newDisplayedPct = Math.max(prevDisplayedPct, serverPct);

          set((state) => ({
            jobs: {
              ...state.jobs,
              [jobId]: {
                ...state.jobs[jobId],
                status: updated.status,
                percentage: serverPct,
                displayedPct: updated.status === "COMPLETED" ? 100 : newDisplayedPct,
                currentStage: updated.currentStage,
                totalCrCount: updated.totalCrCount ?? 0,
                completedCrCount: updated.completedCrCount ?? 0,
                failedCrCount: updated.failedCrCount ?? 0,
                totalFileCount: updated.totalFileCount ?? 0,
                processedFileCount: updated.processedFileCount ?? 0,
                ocrPagesCompleted: updated.ocrPagesCompleted ?? 0,
                ocrPagesTotal: updated.ocrPagesTotal ?? 0,
                elapsedTimeMs: updated.elapsedTimeMs ?? 0,
                errorSummary: updated.errorSummary,
              },
            },
          }));

          if (updated.status === "COMPLETED") {
            stopPolling(jobId);
            useToastStore.getState().showSuccess(
              "Import Complete",
              `Successfully imported ${updated.completedCrCount ?? 0} change requests.`
            );
            window.dispatchEvent(new CustomEvent("cgar-reload-list"));
          } else if (updated.status === "COMPLETED_WITH_ERRORS") {
            stopPolling(jobId);
            useToastStore.getState().showError(
              "Import Completed with Errors",
              updated.errorSummary || "Some files failed to process."
            );
            window.dispatchEvent(new CustomEvent("cgar-reload-list"));
          } else if (updated.status === "FAILED") {
            stopPolling(jobId);
            useToastStore.getState().showError(
              "Import Failed",
              updated.errorSummary || "The import job failed."
            );
          }
        } catch (pollErr) {
          console.error("Error polling ingestion job", pollErr);
        }
      };

      // Quick check at 3s for immediate update
      const initialTimer = setTimeout(pollFn, 3000);

      // Polling every 30 seconds
      let elapsed = 0;
      const intervalId = setInterval(async () => {
        elapsed += 30_000;
        if (elapsed > 1800_000) {
          clearTimeout(initialTimer);
          stopPolling(jobId);
          set((state) => ({
            jobs: {
              ...state.jobs,
              [jobId]: {
                ...state.jobs[jobId],
                status: "FAILED",
                errorSummary: "Timed out waiting for ingestion to complete.",
              },
            },
          }));
          return;
        }

        await pollFn();
      }, 30_000);

      _intervals.set(jobId, intervalId);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Could not start the import.";
      useToastStore.getState().showError("Import Failed", msg);
      throw err;
    }
  },

  dismiss: (jobId: string) => {
    stopPolling(jobId);
    set((state) => {
      const next = { ...state.jobs };
      delete next[jobId];
      return { jobs: next };
    });
  },
}));

